import { useEffect, useRef, useState } from 'react';
import { Button, Textarea } from '@tark/shared/components';
import {
  useFinishScheduleAttendanceRecap,
  useSaveScheduleAttendanceRecap,
  useScheduleAttendanceRecap,
} from '@tark/shared/hooks';
import type { TenantSessionDetailResponse, TenantSessionRosterEntry } from '@tark/shared/types';
import { Check, ChevronDown, ChevronUp, Loader2, Plus, X } from 'lucide-react';
import {
  addCustomRecapDrill,
  buildSessionRecapPayload,
  createSessionRecapDraft,
  toggleRecapDrill,
  type SessionRecapDraft,
} from './utils/sessionRecapDraft';

export type SessionRecapMode = 'finish' | 'edit';

interface SessionRecapEditorProps {
  occurrenceId: number;
  entry: TenantSessionRosterEntry;
  mode: SessionRecapMode;
  onClose: () => void;
  onSaved: (session: TenantSessionDetailResponse) => void;
}

export default function SessionRecapEditor({
  occurrenceId,
  entry,
  mode,
  onClose,
  onSaved,
}: SessionRecapEditorProps) {
  const query = useScheduleAttendanceRecap(occurrenceId, entry.bookingId, true);
  const saveRecap = useSaveScheduleAttendanceRecap();
  const finishRecap = useFinishScheduleAttendanceRecap();
  const savingRef = useRef(false);
  const [draft, setDraft] = useState<SessionRecapDraft | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) setDraft(createSessionRecapDraft(query.data));
  }, [query.data]);

  const saving = saveRecap.isPending || finishRecap.isPending;

  const submit = async () => {
    if (!draft || savingRef.current) return;
    if (draft.homeworkEnabled && !draft.homeworkTitle.trim()) {
      setErrorMessage('Add a homework title or turn homework off.');
      return;
    }

    savingRef.current = true;
    setErrorMessage(null);
    try {
      const variables = {
        occurrenceId,
        bookingId: entry.bookingId,
        payload: buildSessionRecapPayload(draft),
      };
      const shouldShare = hasFamilyVisibleContent(draft) && !query.data?.familySharedAt;
      const response = mode === 'finish' || shouldShare
        ? await finishRecap.mutateAsync(variables)
        : await saveRecap.mutateAsync(variables);
      onSaved(response.session);
      onClose();
    } catch {
      setErrorMessage(mode === 'finish'
        ? 'Could not finish this session. Try again.'
        : hasFamilyVisibleContent(draft) && !query.data?.familySharedAt
          ? 'Could not share this recap. Try again.'
        : 'Could not save this recap. Try again.');
    } finally {
      savingRef.current = false;
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`session-recap-editor-${entry.bookingId}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-text">{mode === 'finish' ? 'Finish session' : 'Session recap'}</p>
          <p className="text-sm text-text-muted">{entry.athleteDisplayName}</p>
        </div>
        <button
          type="button"
          aria-label="Close session recap"
          disabled={saving}
          onClick={onClose}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-text-muted hover:text-text disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {query.isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-text-muted">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading recap...
        </div>
      ) : null}

      {query.isError && !draft ? (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
          <p className="text-sm font-medium text-amber-700">Could not load this session recap.</p>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => query.refetch()}>Retry</Button>
        </div>
      ) : null}

      {draft ? (
        <div className="mt-4 space-y-4">
          <section>
            <p className="text-sm font-semibold text-text">Completed today</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {(query.data?.drillOptions ?? []).map((option) => {
                const selected = draft.completedDrills.some((item) => item.drillId === option.drillId);
                return (
                  <button
                    key={option.drillId}
                    type="button"
                    disabled={saving}
                    onClick={() => setDraft(toggleRecapDrill(draft, option))}
                    className={`flex min-h-11 items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium ${selected ? 'border-primary/40 bg-primary/5 text-primary' : 'border-border bg-surface text-text'}`}
                  >
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${selected ? 'border-primary bg-primary text-white' : 'border-border'}`}>
                      {selected ? <Check className="h-3.5 w-3.5" /> : null}
                    </span>
                    {option.label}
                  </button>
                );
              })}
            </div>

            {draft.completedDrills.filter((item) => item.drillId == null).map((item) => (
              <div key={item.label} className="mt-2 flex min-h-10 items-center gap-2 rounded-lg bg-primary/5 px-3 text-sm text-text">
                <Check className="h-4 w-4 text-primary" />
                <span className="flex-1">{item.label}</span>
                <button
                  type="button"
                  aria-label={`Remove ${item.label}`}
                  onClick={() => setDraft({
                    ...draft,
                    completedDrills: draft.completedDrills.filter((drill) => drill !== item),
                  })}
                  className="inline-flex h-8 w-8 items-center justify-center text-text-muted"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}

            <div className="mt-2 flex gap-2">
              <input
                value={draft.customDrillLabel}
                maxLength={160}
                disabled={saving}
                placeholder="Add another drill"
                onChange={(event) => setDraft({ ...draft, customDrillLabel: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    setDraft(addCustomRecapDrill(draft));
                  }
                }}
                className="min-h-11 flex-1 rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none focus:border-primary"
              />
              <button
                type="button"
                aria-label="Add drill"
                disabled={saving || !draft.customDrillLabel.trim()}
                onClick={() => setDraft(addCustomRecapDrill(draft))}
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-white disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </section>

          <OptionalHeader
            label="Family note"
            expanded={draft.noteExpanded}
            onClick={() => setDraft({ ...draft, noteExpanded: !draft.noteExpanded })}
          />
          {draft.noteExpanded ? (
            <Textarea
              label="Family note"
              value={draft.progressNote}
              maxLength={2000}
              rows={3}
              placeholder="A short update the family can read"
              onChange={(event) => setDraft({ ...draft, progressNote: event.target.value })}
            />
          ) : null}

          <OptionalHeader
            label="Homework"
            expanded={draft.homeworkExpanded}
            onClick={() => setDraft({ ...draft, homeworkExpanded: !draft.homeworkExpanded })}
          />
          {draft.homeworkExpanded ? (
            <div className="space-y-3">
              <CheckboxRow
                label="Assign practice"
                checked={draft.homeworkEnabled}
                onChange={(homeworkEnabled) => setDraft({ ...draft, homeworkEnabled })}
              />
              {draft.homeworkEnabled ? (
                <>
                  <input
                    value={draft.homeworkTitle}
                    maxLength={240}
                    placeholder="What should they practice?"
                    onChange={(event) => setDraft({ ...draft, homeworkTitle: event.target.value })}
                    className="min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none focus:border-primary"
                  />
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-text-muted">
                    Due (optional)
                    <input
                      type="datetime-local"
                      value={draft.homeworkDueAt}
                      onChange={(event) => setDraft({ ...draft, homeworkDueAt: event.target.value })}
                      className="mt-1 min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-sm font-normal normal-case tracking-normal text-text outline-none focus:border-primary"
                    />
                  </label>
                  <CheckboxRow
                    label="Share with family"
                    checked={draft.shareWithFamily}
                    onChange={(shareWithFamily) => setDraft({ ...draft, shareWithFamily })}
                  />
                </>
              ) : null}
            </div>
          ) : null}

          {errorMessage ? <p className="text-sm font-medium text-red-600">{errorMessage}</p> : null}
          <div className="flex justify-end">
            <Button isLoading={saving} disabled={saving} onClick={() => void submit()}>
              {submitLabel(mode, draft, query.data?.familySharedAt)}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function hasFamilyVisibleContent(draft: SessionRecapDraft): boolean {
  return draft.completedDrills.length > 0
    || Boolean(draft.progressNote.trim())
    || Boolean(draft.homeworkEnabled && draft.homeworkTitle.trim() && draft.shareWithFamily);
}

function hasAnyRecapContent(draft: SessionRecapDraft): boolean {
  return draft.completedDrills.length > 0
    || Boolean(draft.progressNote.trim())
    || Boolean(draft.homeworkEnabled && draft.homeworkTitle.trim());
}

function submitLabel(mode: SessionRecapMode, draft: SessionRecapDraft, familySharedAt?: string | null): string {
  if (mode === 'finish') {
    return hasFamilyVisibleContent(draft)
      ? 'Finish & share'
      : hasAnyRecapContent(draft)
        ? 'Finish session'
        : 'Check out';
  }
  return hasFamilyVisibleContent(draft) && !familySharedAt ? 'Save & share' : 'Save recap';
}

function OptionalHeader({ label, expanded, onClick }: { label: string; expanded: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-11 w-full items-center justify-between border-t border-border pt-3 text-left text-sm font-semibold text-text"
    >
      {label}
      {expanded ? <ChevronUp className="h-4 w-4 text-text-muted" /> : <ChevronDown className="h-4 w-4 text-text-muted" />}
    </button>
  );
}

function CheckboxRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="flex min-h-11 items-center gap-3 text-sm font-medium text-text">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 accent-primary"
      />
      {label}
    </label>
  );
}
